// @flow
import React, {useState, useEffect} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
  Typography,
  Divider,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Box,
  CircularProgress,
} from '@material-ui/core';
import {colors} from '../../../../../assets/styles/colors';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import CustomAlert from '../../../../../elements/CustomAlert/CustomAlert';
import {useStoreStockSetting} from '../../hooks/useStoreStockSetting';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  settingSectionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  settingSectionTitle: {
    fontWeight: 600,
    color: colors.grey900,
  },
  settingTitle: {
    fontWeight: 600,
    fontSize: '0.875rem',
    color: colors.grey900,
  },
  description: {
    fontSize: '0.875rem',
    color: colors.grey800,
    marginBottom: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(2, 0),
    width: '100%',
    borderColor: colors.grey200,
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey900,
    marginBottom: theme.spacing(1),
  },
  sectionSubtitle: {
    fontSize: '0.875rem',
    color: colors.grey700,
    marginBottom: theme.spacing(2),
  },
  radioGroup: {
    marginLeft: theme.spacing(1),
  },
  radioOption: {
    marginBottom: theme.spacing(1),
  },
  radioLabel: {
    fontSize: '0.875rem',
    color: colors.grey900,
    fontWeight: 500,
  },
  radioDescription: {
    fontSize: '0.8125rem',
    color: colors.grey600,
  },
  warningAlert: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: '#FEF7E6',
    border: `1px solid ${colors.warning}`,
    '& .MuiAlert-icon': {
      color: colors.darkWarning,
    },
  },
  infoAlert: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: '#E6F7FF',
    border: `1px solid ${colors.info}`,
    '& .MuiAlert-icon': {
      color: colors.info,
    },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  saveButton: {
    marginLeft: theme.spacing(2),
  },
  reversionInfoBox: {
    backgroundColor: '#F8F9FA',
    border: `1px solid ${colors.grey200}`,
    borderRadius: '8px',
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  reversionTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: colors.grey900,
    marginBottom: theme.spacing(1),
  },
  reversionText: {
    fontSize: '0.8125rem',
    color: colors.grey700,
    lineHeight: 1.5,
  },
  scopeInfoBox: {
    backgroundColor: '#fffcf4',
    border: `1px solid ${colors.warning}`,
    borderRadius: '8px',
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  scopeTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: colors.grey900,
    marginBottom: theme.spacing(1),
  },
  scopeText: {
    fontSize: '0.8125rem',
    color: colors.grey800,
    lineHeight: 1.5,
  },
  reversionInfo: {
    fontSize: '0.75rem',
    color: colors.grey600,
    fontStyle: 'italic',
    marginTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(1),
    borderLeft: `2px solid ${colors.grey300}`,
    fontWeight: 600,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  checkboxContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  checkboxLabel: {
    fontSize: '0.875rem',
    color: colors.grey900,
    fontWeight: 500,
  },
  checkboxDescription: {
    fontSize: '0.8125rem',
    color: colors.grey600,
    marginTop: theme.spacing(0.5),
  },
}));

export default function StockReduction() {
  const classes = useStyles();
  const {
    settings,
    loading,
    saving,
    error,
    saveSettings,
  } = useStoreStockSetting();

  const [orderStockReductionState, setOrderStockReductionState] = useState(
    'CONFIRMED'
  );
  const [
    preOrderStockReductionOption,
    setPreOrderStockReductionOption,
  ] = useState('ON_CREATE');
  useEffect(() => {
    if (settings) {
      setOrderStockReductionState(settings.setting.orderState);
      setPreOrderStockReductionOption(
        settings.setting.orderHandling ? 'ON_CONVERT' : 'ON_CREATE'
      );
    }
  }, [settings]);

  const handleOrderStateChange = (event: any) => {
    setOrderStockReductionState(event.target.value);
  };

  const handlePreOrderOptionChange = (event: any) => {
    setPreOrderStockReductionOption(event.target.value);
  };

  const handleSaveOrderConfig = async () => {
    try {
      await saveSettings({
        orderState: orderStockReductionState,
        orderHandling: preOrderStockReductionOption === 'ON_CONVERT',
      });
    } catch (error) {
      console.error('Error saving order config:', error);
    }
  };

  const getOrderStateMessage = () => {
    switch (orderStockReductionState) {
      case 'PENDING':
        return {
          type: 'warning',
          message:
            'Al estar en el primer estado del proceso, la única forma de revertir el stock será cancelando la orden completamente.',
        };
      case 'CONFIRMED':
        return {
          type: 'info',
          message:
            'El stock se reducirá cuando la orden sea aceptada. Para revertir, regresa la orden al estado "Pendiente".',
        };
      case 'PAID':
        return {
          type: 'info',
          message:
            'El stock se reducirá cuando la orden sea marcada como pagada. Para revertir, regresa la orden al estado "Aceptado".',
        };
      default:
        return null;
    }
  };

  const stateMessage = getOrderStateMessage();

  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.settingSectionTitleRow}>
        <div
          className={classes.settingTitle}
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography className={classes.settingSectionTitle}>
            Configuración de stock
          </Typography>
        </div>
      </div>
      <Typography className={classes.description}>
        Define cuándo se debe reducir automáticamente el stock de tus productos
        según el estado de las órdenes y pre-órdenes.
      </Typography>

      {error && (
        <CustomAlert severity="error" className={classes.warningAlert}>
          {error}
        </CustomAlert>
      )}

      <Box className={classes.scopeInfoBox}>
        <Typography className={classes.scopeTitle}>
          <span role="img" aria-label="warning">
            ⚠️
          </span>
          Importante: Alcance de los cambios
        </Typography>
        <Typography className={classes.scopeText}>
          • Estos cambios se aplicarán únicamente a las nuevas órdenes y
          pre-órdenes que se creen a partir de ahora.
        </Typography>
        <Typography className={classes.scopeText}>
          • Las órdenes existentes mantendrán su configuración anterior.
        </Typography>
        <Typography className={classes.scopeText}>
          • Cada orden almacena esta configuración de forma individual para
          evitar problemas de duplicación o incrementos múltiples del stock.
        </Typography>
      </Box>

      <Divider className={classes.divider} />

      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Reducción de stock para Órdenes
        </Typography>
        <Typography className={classes.sectionSubtitle}>
          Selecciona el estado a partir del cual se descontará automáticamente
          el stock del producto de tu inventario.
        </Typography>

        <FormControl component="fieldset">
          <RadioGroup
            value={orderStockReductionState}
            onChange={handleOrderStateChange}
            className={classes.radioGroup}
          >
            <div className={classes.radioOption}>
              <FormControlLabel
                value="PENDING"
                control={<Radio color="primary" />}
                label={
                  <div>
                    <Typography className={classes.radioLabel}>
                      Pendiente
                    </Typography>
                    <Typography className={classes.radioDescription}>
                      El stock se reduce inmediatamente al crear la orden
                    </Typography>
                    <Typography className={classes.reversionInfo}>
                      El stock solo se revertirá cuando se elimine completamente
                      la orden.
                    </Typography>
                  </div>
                }
              />
            </div>
            <div className={classes.radioOption}>
              <FormControlLabel
                value="CONFIRMED"
                control={<Radio color="primary" />}
                label={
                  <div>
                    <Typography className={classes.radioLabel}>
                      Aceptado
                    </Typography>
                    <Typography className={classes.radioDescription}>
                      El stock se reduce cuando aceptas la orden
                    </Typography>
                    <Typography className={classes.reversionInfo}>
                      Al regresar a "Pendiente", el stock se revertirá
                      automáticamente.
                    </Typography>
                  </div>
                }
              />
            </div>
            <div className={classes.radioOption}>
              <FormControlLabel
                value="PAID"
                control={<Radio color="primary" />}
                label={
                  <div>
                    <Typography className={classes.radioLabel}>
                      Pagado
                    </Typography>
                    <Typography className={classes.radioDescription}>
                      El stock se reduce cuando la orden es marcada como pagada
                    </Typography>
                    <Typography className={classes.reversionInfo}>
                      Al regresar a "Aceptado", el stock se revertirá
                      automáticamente.
                    </Typography>
                  </div>
                }
              />
            </div>
          </RadioGroup>
        </FormControl>

        {stateMessage && (
          <CustomAlert
            severity={stateMessage.type}
            className={
              stateMessage.type === 'warning'
                ? classes.warningAlert
                : classes.infoAlert
            }
          >
            {stateMessage.message}
          </CustomAlert>
        )}
      </div>

      <Divider className={classes.divider} />

      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>
          Reducción de stock para Pre-órdenes
        </Typography>
        <Typography className={classes.sectionSubtitle}>
          Define cuándo se debe descontar el stock para las pre-órdenes de tus
          productos.
        </Typography>

        <FormControl component="fieldset">
          <RadioGroup
            value={preOrderStockReductionOption}
            onChange={handlePreOrderOptionChange}
            className={classes.radioGroup}
          >
            <div className={classes.radioOption}>
              <FormControlLabel
                value="ON_CREATE"
                control={<Radio color="primary" />}
                label={
                  <div>
                    <Typography className={classes.radioLabel}>
                      Al crear la pre-orden
                    </Typography>
                    <Typography className={classes.radioDescription}>
                      El stock se reduce inmediatamente al generar la pre-orden
                    </Typography>
                    <Typography className={classes.reversionInfo}>
                      Al eliminar la pre-orden, el stock se revertirá
                      automáticamente.
                    </Typography>
                  </div>
                }
              />
            </div>
            <div className={classes.radioOption}>
              <FormControlLabel
                value="ON_CONVERT"
                control={<Radio color="primary" />}
                label={
                  <div>
                    <Typography className={classes.radioLabel}>
                      Al convertir a orden
                    </Typography>
                    <Typography className={classes.radioDescription}>
                      El stock se reduce cuando la pre-orden se convierte en una
                      orden regular, siguiendo la configuración de órdenes
                    </Typography>
                  </div>
                }
              />
            </div>
          </RadioGroup>
        </FormControl>

        <div className={classes.buttonContainer}>
          <CustomButton
            variant="contained"
            onClick={handleSaveOrderConfig}
            className={classes.saveButton}
            disabled={saving}
          >
            {saving ? (
              <>
                <CircularProgress size={16} style={{marginRight: 8}} />
                Guardando...
              </>
            ) : (
              'Guardar configuración'
            )}
          </CustomButton>
        </div>
      </div>
    </div>
  );
}
